import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  MapPin, 
  Navigation, 
  Car, 
  PartyPopper,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  Users,
  Heart,
  Music
} from 'lucide-react';

const OnboardingTutorial = ({ userName, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const steps = [
    {
      title: "Welcome to RollM8s!",
      subtitle: `Hey ${userName?.split(' ')[0] || 'there'}!`,
      description: "The home of the UK roller skating community. Share rides to events, discover what's on, and showcase your brand \u2014 whether you're an organiser, DJ, or skater.",
      icon: <Sparkles className="w-16 h-16 text-primary" />,
      gradient: "from-primary/20 to-secondary/20"
    },
    {
      title: "What Can You Do?",
      subtitle: "Six ways to roll with us",
      description: "Ride, organise, DJ, or just discover \u2014 there's something for everyone.",
      icon: null,
      gradient: "from-secondary/20 to-accent/20",
      actions: [
        { icon: <MapPin className="w-6 h-6" />, label: "Find an Event", desc: "Browse events & book rides", color: "text-primary" },
        { icon: <Navigation className="w-6 h-6" />, label: "Request a Ride", desc: "Event not listed? Ask for a lift!", color: "text-secondary" },
        { icon: <Car className="w-6 h-6" />, label: "Offer a Ride", desc: "Share your car & split fuel costs", color: "text-accent" },
        { icon: <PartyPopper className="w-6 h-6" />, label: "Create an Event", desc: "List your skating event and sell tickets", color: "text-warning" },
        { icon: <Users className="w-6 h-6" />, label: "My Corner", desc: "Your own page to promote events & merch", color: "text-pink-500" },
        { icon: <Music className="w-6 h-6" />, label: "DJ Space", desc: "Share your mixes & build your brand", color: "text-cyan-500" },
        { icon: <Heart className="w-6 h-6" />, label: "Share Events", desc: "Share events with rich link previews", color: "text-red-400" }
      ]
    },
    {
      title: "Ready to Roll?",
      subtitle: "You're all set!",
      description: "Start by finding an event you want to attend, or set up your own corner if you're an organiser or DJ. The community is waiting for you!",
      icon: <Heart className="w-16 h-16 text-pink-500" />,
      gradient: "from-accent/20 to-warning/20",
      tips: [
        "Browse events and book a ride to get rolling",
        "Are you an organiser or DJ? Set up My Corner to showcase your brand",
        "See a skater needing a lift? Offer a ride and help them get there"
      ]
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (dontShowAgain) {
      localStorage.setItem('rollm8s_onboarding_hidden', 'true');
    }
    localStorage.setItem('rollm8s_onboarding_complete', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  // Skip = "I don't want this tour, ever". Marking hidden so it never reappears
  // even if they didn't tick the checkbox. Single-tap dismiss.
  const handleSkip = () => {
    localStorage.setItem('rollm8s_onboarding_hidden', 'true');
    localStorage.setItem('rollm8s_onboarding_complete', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-8 bg-background/80 backdrop-blur-sm animate-fade-in" data-testid="onboarding-modal"
      style={{ touchAction: 'manipulation' }}>
      <Card className="w-full max-w-lg bg-card border-border/50 shadow-2xl flex flex-col" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
        {/* Progress bar */}
        <div className="h-1 bg-border/30 flex-shrink-0">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="p-5 sm:p-8 relative">
            {/* Skip button — prominent pill so users can dismiss in ONE tap */}
            <button
              onClick={handleSkip}
              className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/90 border border-border/60 hover:bg-foreground/10 active:scale-95 transition-all text-sm font-medium text-foreground shadow-md"
              data-testid="onboarding-skip"
              style={{ touchAction: 'manipulation' }}
              aria-label="Skip onboarding tour"
            >
              <X className="w-4 h-4" />
              <span>Skip</span>
            </button>

            {/* Header gradient — decorative only */}
            <div className={`absolute top-0 left-0 right-0 h-32 bg-gradient-to-br ${step.gradient} opacity-50 pointer-events-none`} />

            {/* Step content */}
            <div className="text-center mb-6 relative">
              {step.icon && (
                <div className="mb-4 flex justify-center animate-bounce-slow pointer-events-none">
                  {step.icon}
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-1">{step.subtitle}</p>
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl mb-3 gradient-text">{step.title}</h2>
              <p className="text-sm text-muted-foreground">{step.description}</p>
            </div>

            {/* Step-specific content */}
            {step.actions && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                {step.actions.map((action, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-xl bg-background/50 border border-border/30 text-center"
                  >
                    <div className={`${action.color} mb-2 flex justify-center`}>
                      {action.icon}
                    </div>
                    <p className="font-medium text-sm">{action.label}</p>
                    <p className="text-xs text-muted-foreground">{action.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {step.tips && (
              <div className="space-y-3 mb-4">
                {step.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-primary font-bold text-xs">{idx + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{tip}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Navigation — pinned outside scroll area */}
        <div className="flex-shrink-0 px-5 sm:px-8 pb-5 pt-3 border-t border-border/20">
          {/* Don't show again checkbox */}
          <label className="flex items-center gap-2 justify-center cursor-pointer mb-4">
            <input 
              type="checkbox" 
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-border accent-primary"
            />
            <span className="text-sm text-muted-foreground">Don't show this again</span>
          </label>

          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="rounded-full min-h-[48px] min-w-[90px] text-base"
              data-testid="onboarding-prev"
              style={{ touchAction: 'manipulation' }}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {/* Step indicators */}
            <div className="flex gap-2">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`h-3 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-primary w-8' 
                      : idx < currentStep 
                        ? 'bg-primary/50 w-3' 
                        : 'bg-border w-3'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                />
              ))}
            </div>

            <Button
              onClick={handleNext}
              className="rounded-full bg-primary hover:bg-primary/90 min-h-[48px] min-w-[110px] text-base"
              data-testid="onboarding-next"
              style={{ touchAction: 'manipulation' }}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Let's Go!
                  <Sparkles className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default OnboardingTutorial;
